import type { MissionAssessmentRequestSelectDto } from '@@types/api.types';
import { MissionAssessmentRequestStatus } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AvatarList } from '@components/shared/avatar-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { Badge } from '@components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { countries } from '@utils/constants';
import { formatVersionDate } from '@utils/date.util';
import { FC, useMemo } from 'react';

type MaRequestDetailsCardProps = {
  request: MissionAssessmentRequestSelectDto;
};

export const MaRequestDetailsCard: FC<MaRequestDetailsCardProps> = (props) => {
  const { request } = props;
  const { requester, veteranServices } = request;

  const isCancelled = request.status === MissionAssessmentRequestStatus.CANCELLED;

  const requesterName = useMemo(() => {
    if (!requester?.profile) return null;
    const { firstName, lastName } = requester.profile;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return firstName ?? lastName ?? null;
  }, [requester?.profile]);

  const requestDateText = useMemo(() => {
    return formatVersionDate(request.createdAt);
  }, [request.createdAt]);

  const veteranCountryNames = useMemo(() => {
    if (!request.veteranCountriesOfService || request.veteranCountriesOfService.length === 0) {
      return [];
    }

    return request.veteranCountriesOfService
      .map((code) => {
        const country = countries.find((c) => c.code === code);
        return country?.name ?? code;
      })
      .filter(Boolean);
  }, [request.veteranCountriesOfService]);

  const veteranCountryDisplay = useMemo(() => {
    if (veteranCountryNames.length === 0) {
      return null;
    }
    if (veteranCountryNames.length === 1) {
      return veteranCountryNames[0];
    }
    return veteranCountryNames.join(', ');
  }, [veteranCountryNames]);

  const serviceAvatarItems = useMemo(() => {
    if (!veteranServices || veteranServices.length === 0) {
      return [];
    }

    return veteranServices.map((service) => {
      let tooltip = service.name;
      if (service.parentService?.name) {
        const parentCountryName = service.parentService.name;
        const country = countries.find((c) => c.name === parentCountryName);
        const countryCode = country?.code ?? parentCountryName;
        tooltip = `${countryCode} - ${service.name}`;
      }

      return {
        altText: service.name,
        imageUrl: service.slug ? `/images/services/${service.slug}.png` : undefined,
        initialText: service.name,
        tooltip,
      };
    });
  }, [veteranServices]);

  return (
    <div className="px-4 pb-4">
      {/* Requester */}
      {requesterName && (
        <div className="flex flex-col gap-0.5 mb-3">
          <span className="font-medium text-gray-600">{requesterName}</span>
          <BlueLabel text="Requester" />
        </div>
      )}

      {/* Header Row: Request Date + Status Badge */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-600">{requestDateText}</span>
          <BlueLabel text="Request Date" />
        </div>

        {/* Request Withdrawn Badge (takes priority over Strict Match) */}
        {isCancelled ? (
          <TooltipComponent
            className="text-xs"
            side="top"
            trigger={
              <Badge
                className="shrink-0"
                size="xs"
                variant="destructive"
              >
                Withdrawn
              </Badge>
            }
          >
            The technology owner has cancelled this mission assessment request.
          </TooltipComponent>
        ) : (
          request.isStrictMatch && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-flex">
                    <Badge
                      size="xs"
                      variant="primary"
                    >
                      Strict Match
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Requires exact match for target nation and service criteria</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
      </div>

      {/* Request Details */}
      <div className="text-sm font-medium text-gray-600 space-y-3">
        {/* Use */}
        {request.use && (
          <div className="flex flex-col gap-0.5">
            <span>{request.use.name}</span>
            <BlueLabel text="Use" />
          </div>
        )}

        {/* Domain */}
        {request.domain && (
          <div className="flex flex-col gap-0.5">
            <span>{request.domain.name}</span>
            <BlueLabel text="Domain" />
          </div>
        )}

        {veteranCountryDisplay && (
          <div className="flex flex-col gap-1">
            <span>{veteranCountryDisplay}</span>
            <BlueLabel text={veteranCountryNames.length > 1 ? 'Nations' : 'Nation'} />
          </div>
        )}

        {serviceAvatarItems.length > 0 && (
          <div className="flex flex-col gap-1">
            <AvatarList
              items={serviceAvatarItems}
              limit={3}
              size="sm"
            />
            <BlueLabel text={serviceAvatarItems.length > 1 ? 'Services' : 'Service'} />
          </div>
        )}
      </div>

      {/* Message */}
      {request.message && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <ExpandableText
            className="text-xs text-gray-600"
            text={request.message}
          />
        </div>
      )}
    </div>
  );
};
